import { matchPath } from 'react-router';
import { getPostBySlug } from '../services/blogMeta';
import { getSupportArticleMeta } from '../services/supportMeta';

export interface RouteMeta {
  title: string;
  description: string;
  canonicalPath: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  robots?: string;
}

interface RouteMetaMatcher {
  path: string;
  meta: RouteMeta | ((params: Record<string, string>) => RouteMeta);
}

const SITE_URL = 'https://haveniaq.com';
const DEFAULT_OG_IMAGE = 'https://haven-pro-web-portal.s3.us-west-2.amazonaws.com/Website+Video+Thumbnail.png';

const routeMetaMatchers: RouteMetaMatcher[] = [
  {
    path: '/live-room/:accessCode?',
    meta: {
      title: 'Live Room Air Quality | HAVEN IAQ',
      description: 'Live indoor air quality readings from a HAVEN Room Monitor.',
      canonicalPath: '/live-room',
      ogType: 'website',
      robots: 'noindex,nofollow,noarchive',
    },
  },
  {
    path: '/',
    meta: {
      title: 'HAVEN IAQ | Professional Indoor Air Quality Solutions',
      description:
        'Professionally managed indoor air quality solutions with monitoring, automation, and healthier-home guidance from HAVEN IAQ.',
      canonicalPath: '/',
      ogType: 'website',
    },
  },
  {
    path: '/homeowner-start',
    meta: {
      title: 'Homeowner Start | Buy, Install And Set Up HAVEN IAQ',
      description:
        'Start your HAVEN IAQ journey with homeowner purchase options, installation support, and app setup guidance.',
      canonicalPath: '/homeowner-start',
      ogType: 'website',
    },
  },
  {
    path: '/room-monitor',
    meta: {
      title: 'HAVEN Room Monitor | Smart Indoor Air Quality Monitoring',
      description:
        'Track CO2, PM2.5, humidity, VOCs, NOx, dew point, and temperature with the HAVEN Room Monitor for healthier indoor air.',
      canonicalPath: '/room-monitor',
      ogType: 'website',
    },
  },
  {
    path: '/central-air-monitor',
    meta: {
      title: 'HAVEN Central Air Monitor | Whole-Home IAQ Monitoring',
      description:
        'Monitor whole-home indoor air quality in your HVAC system with the HAVEN Central Air Monitor and active air management platform.',
      canonicalPath: '/central-air-monitor',
      ogType: 'website',
    },
  },
  {
    path: '/central-controller',
    meta: {
      title: 'HAVEN Central Controller | HVAC Automation For IAQ',
      description:
        'See how the HAVEN Central Controller helps HVAC professionals automate ventilation, filtration, and humidity control from real IAQ data.',
      canonicalPath: '/central-controller',
      ogType: 'website',
    },
  },
  {
    path: '/homeowner-store',
    meta: {
      title: 'HAVEN Homeowner Store | IAQ Monitoring And Control Products',
      description:
        'Shop HAVEN indoor air quality monitoring and control products for homeowners, including room monitors, controller bundles, and central air solutions.',
      canonicalPath: '/homeowner-store',
      ogType: 'website',
    },
  },
  {
    path: '/solutions',
    meta: {
      title: 'Indoor Air Quality Solutions | HAVEN IAQ',
      description:
        'Explore HAVEN IAQ solutions for demand-controlled ventilation, active air management, radon mitigation, and wildfire protection.',
      canonicalPath: '/solutions',
      ogType: 'website',
    },
  },
  {
    path: '/residential-demand-control-ventilation-for-humid-climates',
    meta: {
      title: 'Residential Demand Control Ventilation | HAVEN IAQ',
      description:
        'Learn how HAVEN IAQ supports residential demand-controlled ventilation for healthier homes, comfort, and better humidity control.',
      canonicalPath: '/residential-demand-control-ventilation',
      ogType: 'website',
    },
  },
  {
    path: '/residential-demand-control-ventilation',
    meta: {
      title: 'Residential Demand Control Ventilation | HAVEN IAQ',
      description:
        'Learn how HAVEN IAQ supports residential demand-controlled ventilation for healthier homes, comfort, and better humidity control.',
      canonicalPath: '/residential-demand-control-ventilation',
      ogType: 'website',
    },
  },
  {
    path: '/active-air-management',
    meta: {
      title: 'Active Air Management | HAVEN IAQ',
      description:
        'See how HAVEN IAQ combines monitoring and control to actively manage ventilation, filtration, and indoor air quality in real time.',
      canonicalPath: '/active-air-management',
      ogType: 'website',
    },
  },
  {
    path: '/haven-products',
    meta: {
      title: 'Active Air Management | HAVEN IAQ',
      description:
        'See how HAVEN IAQ combines monitoring and control to actively manage ventilation, filtration, and indoor air quality in real time.',
      canonicalPath: '/active-air-management',
      ogType: 'website',
    },
  },
  {
    path: '/smart-home-radon-mitigation',
    meta: {
      title: 'Smart Home Radon Mitigation | HAVEN IAQ',
      description:
        'Understand how HAVEN IAQ supports smart-home radon mitigation with monitoring, automation, and healthier-home decision support.',
      canonicalPath: '/smart-home-radon-mitigation',
      ogType: 'website',
    },
  },
  {
    path: '/wildfire-infiltration-checklist',
    meta: {
      title: 'Wildfire Infiltration Checklist | HAVEN IAQ',
      description:
        'Use the HAVEN wildfire infiltration checklist to reduce smoke exposure and protect indoor air quality during wildfire season.',
      canonicalPath: '/wildfire-infiltration-checklist',
      ogType: 'website',
    },
  },
  {
    path: '/resources',
    meta: {
      title: 'Resources & Support | HAVEN IAQ',
      description:
        'Explore HAVEN resources, blog content, installation guidance, product documentation, and support links for healthier indoor air.',
      canonicalPath: '/resources',
      ogType: 'website',
    },
  },
  {
    path: '/about',
    meta: {
      title: 'About HAVEN IAQ | Team And Mission',
      description:
        'Meet the HAVEN IAQ team and learn how the company approaches healthier homes through monitoring, automation, and HVAC collaboration.',
      canonicalPath: '/about',
      ogType: 'website',
    },
  },
  {
    path: '/about/kevinrhart',
    meta: {
      title: 'Kevin R. Hart | Founder And Inventor, HAVEN IAQ',
      description:
        'Learn more about Kevin Hart, founder of HAVEN IAQ, and the mission behind healthier indoor air quality and smarter residential controls.',
      canonicalPath: '/about/kevinrhart',
      ogType: 'website',
    },
  },
  {
    path: '/contact',
    meta: {
      title: 'Contact HAVEN IAQ | Product And Support Questions',
      description:
        'Contact HAVEN IAQ for product questions, installation help, homeowner support, or HVAC professional inquiries.',
      canonicalPath: '/contact',
      ogType: 'website',
    },
  },
  {
    path: '/support',
    meta: {
      title: 'HAVEN Support | Homeowner Product Help And Guides',
      description: 'Find HAVEN homeowner setup, installation, troubleshooting, product, app, and indoor air quality support articles.',
      canonicalPath: '/support',
      ogType: 'website',
    },
  },
  {
    path: '/support/contact',
    meta: {
      title: 'Contact HAVEN Support | Homeowner Help',
      description: 'Contact HAVEN homeowner support by email, phone, or the HAVEN website contact form.',
      canonicalPath: '/support/contact',
      ogType: 'website',
    },
  },
  {
    path: '/support/pro',
    meta: {
      title: 'HAVEN Pro Support | HVAC Installation And Technical Guides',
      description: 'Find HAVEN Pro installation, commissioning, troubleshooting, sales, and platform guidance for HVAC professionals.',
      canonicalPath: '/support/pro',
      ogType: 'website',
    },
  },
  {
    path: '/support/pro/contact',
    meta: {
      title: 'Contact HAVEN Pro Support | HVAC Professional Help',
      description: 'Contact HAVEN Pro support for installation, commissioning, product, and platform assistance.',
      canonicalPath: '/support/pro/contact',
      ogType: 'website',
    },
  },
  {
    path: '/support/pro/installation',
    meta: {
      title: 'HAVEN Pro Installation Guides | Technical Support',
      description: 'Browse HAVEN Pro installation, commissioning, and troubleshooting guidance for HVAC professionals.',
      canonicalPath: '/support/pro/installation',
      ogType: 'website',
    },
  },
  {
    path: '/return-policy',
    meta: {
      title: 'Return Policy | HAVEN IAQ Orders And Eligibility',
      description:
        'Review HAVEN IAQ return policy details, including eligibility, timing, and support information for product returns.',
      canonicalPath: '/return-policy',
      ogType: 'website',
    },
  },
  {
    path: '/privacy',
    meta: {
      title: 'Privacy Policy | How HAVEN IAQ Handles Your Data',
      description:
        'Read the HAVEN IAQ privacy policy, including how personal information is collected, used, stored, and protected.',
      canonicalPath: '/privacy',
      ogType: 'website',
    },
  },
  {
    path: '/cookies',
    meta: {
      title: 'Cookie Policy | Cookies And Consent At HAVEN IAQ',
      description:
        'Review the HAVEN IAQ cookie policy, including the categories of cookies used and how consent preferences are managed.',
      canonicalPath: '/cookies',
      ogType: 'website',
    },
  },
  {
    path: '/eula',
    meta: {
      title: 'End User License Agreement | HAVEN IAQ',
      description:
        'Review the HAVEN IAQ end user license agreement for the terms that govern use of HAVEN software and services.',
      canonicalPath: '/eula',
      ogType: 'website',
    },
  },
  {
    path: '/tos',
    meta: {
      title: 'Terms Of Service | HAVEN IAQ Website And Products',
      description:
        'Read the HAVEN IAQ terms of service for the conditions that apply to website, product, and service use.',
      canonicalPath: '/tos',
      ogType: 'website',
    },
  },
  {
    path: '/delete-account',
    meta: {
      title: 'Delete Account | Remove Your HAVEN IAQ Data',
      description:
        'Request HAVEN IAQ account deletion and review the process for removing personal data tied to your account.',
      canonicalPath: '/delete-account',
      ogType: 'website',
    },
  },
  {
    path: '/hangout',
    meta: {
      title: 'HAVEN Hangout: Choosing Ventilation Equipment | HAVEN IAQ',
      description:
        'Join the HAVEN Hangout — a live video call for HVAC pros on sizing and selecting ventilation equipment using HAVEN\'s ASHRAE 62.2 Calculator and Equipment Finder.',
      canonicalPath: '/hangout',
      ogType: 'website',
    },
  },
  {
    path: '/become-a-pro',
    meta: {
      title: 'Become A HAVEN Pro | IAQ Training For HVAC Contractors',
      description:
        'Discover the HAVEN pro program for HVAC professionals, including training, distribution, automation tools, and clean-air services.',
      canonicalPath: '/become-a-pro',
      ogType: 'website',
    },
  },
  {
    path: '/haven-iaq-start',
    meta: {
      title: 'HAVEN IAQ Start | Get Started With HAVEN',
      description:
        'Start with HAVEN IAQ and find the right next step for homeowners, HVAC pros, and indoor air quality projects.',
      canonicalPath: '/haven-iaq-start',
      ogType: 'website',
    },
  },
  {
    path: '/prostore',
    meta: {
      title: 'HAVEN Pro Store | Contractor Products',
      description:
        'Browse HAVEN products for HVAC professionals, including contractor-focused bundles, monitors, and controllers.',
      canonicalPath: '/prostore',
      ogType: 'website',
    },
  },
  {
    path: '/my-portal',
    meta: {
      title: 'My Portal | HAVEN IAQ Homeowner Dashboard',
      description:
        'View the HAVEN homeowner portal experience for monitoring devices, alerts, and indoor air quality system status.',
      canonicalPath: '/my-portal',
      ogType: 'website',
    },
  },
  {
    path: '/blog',
    meta: {
      title: 'HAVEN Blog & News | Indoor Air Quality Insights',
      description:
        'Read indoor air quality insights, product updates, and HVAC industry perspectives from the HAVEN IAQ team.',
      canonicalPath: '/blog',
      ogType: 'website',
    },
  },
  // Individual blog posts are resolved dynamically from the MDX content
  // collection (see getBlogPostRouteMeta below) — no per-post entries are
  // hardcoded here. This matcher is the last-resort fallback for a slug with
  // no matching MDX file.
  {
    path: '/blog/:slug',
    meta: ({ slug }) => {
      const readableSlug = (slug || 'blog').replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

      return {
        title: `${readableSlug} | HAVEN Blog`,
        description: `Read "${readableSlug}" on the HAVEN IAQ blog for indoor air quality insights and product updates.`,
        canonicalPath: `/blog/${slug || ''}`.replace(/\/+$/, ''),
        ogType: 'article',
      };
    },
  },
];

const normalizePath = (path: string): string => {
  if (!path || path === '/') {
    return '/';
  }

  const stripped = path.replace(/\/+$/, '');
  return stripped.startsWith('/') ? stripped : `/${stripped}`;
};

const getBlogPostRouteMeta = (path: string): RouteMeta | null => {
  const blogMatch = matchPath({ path: '/blog/:slug', end: true }, path);
  const slug = blogMatch?.params?.slug;

  if (!slug) {
    return null;
  }

  const post = getPostBySlug(slug);

  if (!post) {
    return null;
  }

  // Prefer the post's own featured image for the social/search preview;
  // fall back to the site default when a post has none.
  const featuredImage = post.featuredImage
    ? post.featuredImage.startsWith('http')
      ? post.featuredImage
      : `${SITE_URL}${post.featuredImage}`
    : DEFAULT_OG_IMAGE;

  return {
    title: `${post.title} | HAVEN Blog`,
    description: post.excerpt,
    canonicalPath: `/blog/${post.slug}`,
    ogType: 'article',
    ogImage: featuredImage,
  };
};

export const matchRouteMeta = (path: string): RouteMeta | null => {
  const normalizedPath = normalizePath(path);

  for (const matcher of routeMetaMatchers) {
    const match = matchPath({ path: matcher.path, end: true }, normalizedPath);

    if (!match) {
      continue;
    }

    const resolved = typeof matcher.meta === 'function' ? matcher.meta(match.params as Record<string, string>) : matcher.meta;

    return {
      ogImage: DEFAULT_OG_IMAGE,
      ogType: 'website',
      ...resolved,
      canonicalPath: normalizePath(resolved.canonicalPath),
    };
  }

  return null;
};

export const getRouteMeta = (path: string): RouteMeta => {
  const normalizedPath = normalizePath(path);
  const supportArticleMeta = getSupportArticleMeta(normalizedPath);

  if (supportArticleMeta) {
    return {
      ...supportArticleMeta,
      ogImage: DEFAULT_OG_IMAGE,
    };
  }

  const blogPostMeta = getBlogPostRouteMeta(normalizedPath);

  if (blogPostMeta) {
    return blogPostMeta;
  }

  const matchedMeta = matchRouteMeta(normalizedPath);

  if (matchedMeta) {
    return matchedMeta;
  }

  return {
    title: 'HAVEN IAQ | Professional Indoor Air Quality Solutions',
    description:
      'Professionally managed indoor air quality solutions with monitoring, automation, and healthier-home guidance from HAVEN IAQ.',
    canonicalPath: normalizedPath,
    ogType: 'website',
    ogImage: DEFAULT_OG_IMAGE,
  };
};

export const getCanonicalUrl = (path: string): string => {
  const meta = getRouteMeta(path);
  return `${SITE_URL}${meta.canonicalPath === '/' ? '' : meta.canonicalPath}`;
};
